import { useEffect, useState } from "react";
import { IconChevronDown } from "../layout/Icons";
import { ToolCallRow } from "./ToolCallRow";
import { PlanCard } from "./PlanCard";
import type { ChatTurn } from "./types";

/** Re-renders this component once a second so streaming elapsed
 * timestamps tick. Returns the current Date.now() value. */
function useTickWhile(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}

function formatClock(ms: number): string {
  const d = new Date(ms);
  let h = d.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${h}:${mm} ${ampm}`;
}

/**
 * Replit-style turn: user pill on top, then a collapsible "artifact"
 * card with the agent's narration + numbered tool steps + status.
 */
export function TurnView({
  turn,
  onRetry,
  onPickPlanOption,
}: {
  turn: ChatTurn;
  onRetry?: () => void;
  onPickPlanOption?: (prompt: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <UserBubble text={turn.userPrompt} images={turn.images} />
      <ArtifactBlock turn={turn} onRetry={onRetry} onPickPlanOption={onPickPlanOption} />
    </div>
  );
}

function UserBubble({
  text,
  images,
}: {
  text: string;
  images?: ReadonlyArray<{ dataUrl: string; name: string }>;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, alignSelf: "flex-start", maxWidth: "92%" }}>
      {images && images.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {images.map((img, i) => (
            <a
              key={`${i}-${img.name}`}
              href={img.dataUrl}
              target="_blank"
              rel="noreferrer noopener"
              style={{
                width: 84,
                height: 84,
                borderRadius: 8,
                overflow: "hidden",
                border: "1px solid var(--border-default)",
                background: "var(--bg-canvas)",
                display: "block",
              }}
              title={img.name}
            >
              <img
                src={img.dataUrl}
                alt={img.name}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            </a>
          ))}
        </div>
      )}
      <div
        style={{
          background: "var(--accent-blue)",
          color: "var(--text-on-accent)",
          borderRadius: 10,
          padding: "8px 12px",
          fontSize: 13,
          lineHeight: 1.5,
          wordBreak: "break-word",
        }}
      >
        {text}
      </div>
    </div>
  );
}

function ArtifactBlock({
  turn,
  onRetry,
  onPickPlanOption,
}: {
  turn: ChatTurn;
  onRetry?: () => void;
  onPickPlanOption?: (prompt: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const isStreaming = turn.status === "streaming";
  const stepCount = turn.toolCalls.length;
  const title =
    stepCount === 0 && !turn.assistantText
      ? "Thinking…"
      : titleFromTurn(turn);
  const now = useTickWhile(isStreaming);
  const elapsedMs = (turn.endedAt ?? now) - turn.startedAt;

  return (
    <div
      style={{
        border: "1px solid var(--border-default)",
        borderRadius: 12,
        background: "var(--bg-panel-2)",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          background: "transparent",
          border: "none",
          padding: "10px 12px",
          cursor: "pointer",
          color: "var(--text-primary)",
          textAlign: "left",
        }}
      >
        <SparkleGlyph spinning={isStreaming} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--text-tertiary)",
              marginTop: 2,
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {stepCount > 0 && (
              <span>
                {stepCount} step{stepCount === 1 ? "" : "s"}
              </span>
            )}
            {turn.status === "complete" && (
              <span>
                {turn.iterations} iteration{turn.iterations === 1 ? "" : "s"}
              </span>
            )}
            <span>
              {isStreaming ? (
                <>● {formatDuration(elapsedMs)}</>
              ) : (
                <>{formatDuration(elapsedMs)} · {formatClock(turn.startedAt)}</>
              )}
            </span>
            {turn.modelOverride && (
              <span style={{ color: "var(--accent-purple)" }}>{turn.modelOverride}</span>
            )}
          </div>
        </div>
        <span
          style={{
            color: "var(--text-tertiary)",
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 120ms",
            display: "inline-flex",
          }}
        >
          <IconChevronDown size={14} />
        </span>
      </button>

      {open && (
        <div style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
          {turn.assistantText && (
            <div
              style={{
                color: "var(--text-secondary)",
                fontSize: 12.5,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {turn.assistantText}
            </div>
          )}

          {turn.toolCalls.length > 0 && (
            <ol
              style={{
                margin: 0,
                padding: 0,
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              {turn.toolCalls.map((tc, i) =>
                tc.name === "propose_plan" && onPickPlanOption ? (
                  <li key={`${i}-${tc.id}`}>
                    <PlanCard call={tc} onPick={onPickPlanOption} />
                  </li>
                ) : (
                  <ToolCallRow key={`${i}-${tc.id}`} call={tc} />
                ),
              )}
            </ol>
          )}

          {isStreaming && (
            <div
              style={{
                color: "var(--text-tertiary)",
                fontSize: 11,
                fontStyle: "italic",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  background: "var(--accent-blue)",
                  display: "inline-block",
                  animation: "vp-pulse 1.2s ease-in-out infinite",
                }}
              />
              Working…
            </div>
          )}

          {turn.status === "error" && turn.error && (
            <div
              style={{
                padding: 10,
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.4)",
                borderRadius: 6,
                color: "var(--accent-red)",
                fontSize: 12,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <span>{turn.error}</span>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  style={{
                    alignSelf: "flex-start",
                    padding: "5px 10px",
                    background: "linear-gradient(135deg,#8B5CF6,#6366F1)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  ✦ Retry on Sonnet 4.5
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function titleFromTurn(turn: ChatTurn): string {
  if (turn.toolCalls.length === 0 && turn.assistantText) {
    return firstLine(turn.assistantText) ?? "Working…";
  }
  if (turn.toolCalls.length > 0) {
    const last = turn.toolCalls[turn.toolCalls.length - 1]!;
    const verb = verbFor(last.name);
    const target = describeTarget(last);
    return `${verb} ${target}`.trim();
  }
  return "Working…";
}

function verbFor(name: string): string {
  switch (name) {
    case "write_file":
      return "Writing";
    case "edit_file":
      return "Editing";
    case "delete_file":
      return "Deleting";
    case "read_file":
      return "Reading";
    case "list_files":
      return "Listing";
    case "add_dependency":
      return "Adding dependency";
    case "plan":
      return "Planning";
    default:
      return name.replace(/_/g, " ");
  }
}

function describeTarget(call: { args?: unknown }): string {
  const args = (call.args as Record<string, unknown> | undefined) ?? undefined;
  if (!args) return "";
  if (typeof args["path"] === "string") return args["path"] as string;
  if (typeof args["name"] === "string") return args["name"] as string;
  if (typeof args["package"] === "string") return args["package"] as string;
  return "";
}

function firstLine(s: string): string | undefined {
  const line = s.split("\n").find((l) => l.trim().length > 0);
  return line ? (line.length > 60 ? `${line.slice(0, 59)}…` : line) : undefined;
}

function SparkleGlyph({ spinning }: { spinning: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: 18,
        height: 18,
        borderRadius: 5,
        background: "linear-gradient(135deg,#8B5CF6,#6366F1)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        flexShrink: 0,
        animation: spinning ? "vp-spin 2.4s linear infinite" : "none",
      }}
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2 14 9 21 11 14 13 12 20 10 13 3 11 10 9Z" />
      </svg>
    </span>
  );
}
